import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { useGenerateEmbeddings } from "@/hooks/useGenerateEmbeddings";
import { useEmbeddingsSetup } from "@/hooks/useEmbeddingsSetup";
import { Candidate } from "@/types/candidate";
import { useToast } from "@/hooks/use-toast";

export function CandidateEmbeddingStatus({ 
  candidate,
  onUpdate
}: { 
  candidate: Candidate;
  onUpdate?: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasEmbedding, setHasEmbedding] = useState<boolean | null>(null);
  const { generateCandidateEmbeddings } = useGenerateEmbeddings();
  const { checkEmbeddingColumn } = useEmbeddingsSetup();
  const { toast } = useToast();

  // Check if embeddings column exists and if this candidate has embeddings
  useEffect(() => {
    const checkEmbeddings = async () => {
      if (!candidate) return;
      
      // Check for existence of embedding directly from candidate data
      if (candidate.embedding) {
        setHasEmbedding(true);
        return;
      }
      
      // Otherwise, check if the embedding column exists at all
      const columnExists = await checkEmbeddingColumn('candidates');
      setHasEmbedding(columnExists ? false : null);
    };
    
    checkEmbeddings();
  }, [candidate]);

  const handleGenerateEmbeddings = async () => {
    if (!candidate) return;
    
    setIsLoading(true);
    try {
      // Format candidate data for embeddings
      const textContent = [
        candidate.name,
        candidate.function || '',
        candidate.skills?.join(' ') || '',
        candidate.ai_synthesis || ''
      ].filter(Boolean).join(' ');
      
      if (textContent.trim().length === 0) {
        toast({
          title: "Error",
          description: "Not enough textual data for embedding generation",
          variant: "destructive",
        });
        return;
      }
      
      // Generate embeddings
      const success = await generateCandidateEmbeddings(candidate.id, textContent);
      
      if (success) {
        setHasEmbedding(true);
        toast({
          title: "Success",
          description: "Embeddings generated successfully",
        });
        
        if (onUpdate) {
          onUpdate();
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to generate embeddings",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error generating embeddings:", error);
      toast({
        title: "Error",
        description: "An error occurred while generating embeddings",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-3 mt-2">
      {hasEmbedding === null && (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <XCircle className="h-4 w-4 text-amber-500" />
          Embedding feature not available
        </div>
      )}
      
      {hasEmbedding === false && (
        <>
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <XCircle className="h-4 w-4 text-red-500" />
            No embedding
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            className="gap-2"
            onClick={handleGenerateEmbeddings}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Generate Embedding
          </Button>
        </>
      )}
      
      {hasEmbedding === true && (
        <>
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            Embedding available
          </div>
          <Button 
            size="sm" 
            variant="outline" 
            className="gap-2"
            onClick={handleGenerateEmbeddings}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Regenerate
          </Button>
        </>
      )}
    </div>
  );
}
